import { throttle, delay, extend, isObject, isFunction, get } from 'lodash-es';
import debug from 'debug';
import { watch } from 'vue';
import { ComponentValueCache } from '../caching';
import { ComponentDataSpec } from '../schemas';

const log = debug('itp:core:useDataLoader');

export interface DataLoaderMap {
  [key: string]: () => Promise<any>;
}

interface UseDataLoaderOptions {
  state: any;
  friendlyName: () => string;
  dataSpecs: ComponentDataSpec[];
  loaders: DataLoaderMap;
  context: any;
  ux?: any;
  helpers: any;
}

export function useDataLoader({
  state,
  friendlyName,
  dataSpecs,
  loaders,
  context,
  ux,
  helpers,
}: UseDataLoaderOptions) {
  const DEFAULT_LOADING_INDICATOR_DELAY_MS = 300;
  const loadingIndicatorDelayMs =
    ux && typeof ux.loadingIndicatorDelay === 'number'
      ? ux.loadingIndicatorDelay
      : DEFAULT_LOADING_INDICATOR_DELAY_MS;
  let pendingIndicatorTimer: ReturnType<typeof setTimeout> | null = null;

  const cancelPendingIndicator = () => {
    if (pendingIndicatorTimer !== null) {
      clearTimeout(pendingIndicatorTimer);
      pendingIndicatorTimer = null;
    }
  };

  const onLoadStart = () => {
    if (state.showLoadingIndicator?.value) {
      return;
    }
    cancelPendingIndicator();
    if (loadingIndicatorDelayMs <= 0) {
      if (state.showLoadingIndicator) {
        state.showLoadingIndicator.value = true;
      }
      return;
    }
    pendingIndicatorTimer = setTimeout(() => {
      pendingIndicatorTimer = null;
      if (state.loading?.value || state.reloading?.value) {
        if (state.showLoadingIndicator) {
          state.showLoadingIndicator.value = true;
        }
      }
    }, loadingIndicatorDelayMs);
  };

  const onLoadEnd = () => {
    cancelPendingIndicator();
    if (state.showLoadingIndicator) {
      state.showLoadingIndicator.value = false;
    }
  };

  const touchLastLoaded = () => {
    state.loading.value = false;
    state.reloading.value = false;
    state.loaded.value = true;
    state.error.value = null;
    state.lastLoaded.value = Math.round(Date.now() / 1000);
    onLoadEnd();
    log('[%s] lastloaded: %s', friendlyName(), state.lastLoaded.value);
  };

  const handleError = (err: any) => {
    console.error(err);
    const useGlobalModal = ux && ux.errorHandling === 'global';
    if (useGlobalModal && helpers.$imodal?.value) {
      helpers.$imodal.value.error({ error: err });
    } else {
      state.error.value = err;
    }

    state.loading.value = false;
    state.reloading.value = false;
    onLoadEnd();
  };

  const _loadFromCache = async (name: string, spec: ComponentDataSpec) => {
    if (spec.cache) {
      const cacheInstance = new ComponentValueCache(
        context,
        name,
        spec.cache.key,
      );
      const value = cacheInstance.getValue();

      if (value != null) {
        log('[%s] from cache: %s', friendlyName(), name);
        return value;
      }
    }
    return undefined;
  };

  let cacheWatchersRegistered = false;

  const _registerCacheWatchers = () => {
    if (cacheWatchersRegistered) {
      return;
    }
    cacheWatchersRegistered = true;

    for (const spec of dataSpecs) {
      if (!spec.cache) {
        continue;
      }
      const name = spec.name;
      const dataRef = state[name];
      if (!dataRef) {
        continue;
      }

      const cacheInstance = new ComponentValueCache(
        context,
        name,
        spec.cache.key,
      );

      watch(
        dataRef,
        (newValue: any) => {
          cacheInstance.persist(newValue);
        },
        { deep: true },
      );

      log('[%s] registered cache watcher for: %s', friendlyName(), name);
    }
  };

  const _loadDefaultData = async (name: string) => {
    const loaderFn = loaders[name];
    if (isFunction(loaderFn)) {
      return await loaderFn();
    }
    return undefined;
  };

  const _loadData = async (options: { useCache: boolean }) => {
    const result: Record<string, any> = {};
    const { useCache } = options;

    const asyncSpecNames = new Set(
      dataSpecs
        .filter((spec) => isFunction(loaders[spec.name]))
        .map((spec) => spec.name),
    );

    const completed = new Set<string>();

    const loadSpec = async (spec: ComponentDataSpec) => {
      const name = spec.name;
      const currentValue = state[name]?.value;
      const uiSkipComponentReload = get(
        currentValue,
        'uiSkipComponentReload',
        false,
      );

      if (uiSkipComponentReload) {
        completed.add(name);
        return;
      }

      if (useCache) {
        const cached = await _loadFromCache(name, spec);
        if (cached != null) {
          const defaultValue = await _loadDefaultData(name);

          if (isObject(defaultValue) && isObject(cached)) {
            extend({}, defaultValue, cached);
            log('[%s] extend cache with default value: %o', defaultValue);
          }

          if (state[name]) {
            state[name].value = cached;
          }
          result[name] = cached;
          completed.add(name);
          return;
        }
      }

      const defaultValue = await _loadDefaultData(name);
      if (defaultValue != null) {
        if (state[name]) {
          state[name].value = defaultValue;
        }
        result[name] = defaultValue;
      }
      completed.add(name);
    };

    let pendingSpecs = [...dataSpecs];

    while (pendingSpecs.length > 0) {
      const ready: ComponentDataSpec[] = [];
      const notReady: ComponentDataSpec[] = [];

      for (const spec of pendingSpecs) {
        const asyncDeps = (spec.dependsOn ?? []).filter((dep) =>
          asyncSpecNames.has(dep),
        );
        const allDepsCompleted = asyncDeps.every((dep) => completed.has(dep));
        if (allDepsCompleted) {
          ready.push(spec);
        } else {
          notReady.push(spec);
        }
      }

      if (ready.length === 0) {
        log(
          '[%s] wave execution stalled: possible circular dependency',
          friendlyName(),
        );
        break;
      }

      await Promise.all(ready.map(loadSpec));
      pendingSpecs = notReady;
    }

    return result;
  };

  const loadData = (prop?: string | null, opts?: { initial: boolean }) => {
    if (!state.canLoadData.value) {
      log(
        '[%s] loading is already in progress, prevent reload.',
        friendlyName(),
      );
      return Promise.resolve();
    }

    if (!dataSpecs || dataSpecs.length === 0) {
      log('[%s] no data spec, nothing to load.', friendlyName());
      touchLastLoaded();
      return Promise.resolve();
    }

    let delayVal = 0;
    if (opts && opts.initial) {
      state.loading.value = true;
      if (ux && ux.initialLoadDelay) {
        delayVal = ux.initialLoadDelay;
      }
    } else {
      state.reloading.value = true;
    }
    onLoadStart();

    return new Promise<void>((resolve, reject) => {
      const onErr = (e: any) => {
        handleError(e);
        reject(e);
      };

      try {
        log('[%s] loading data...', friendlyName());

        delay(() => {
          _loadData({ useCache: (opts && opts.initial) || false })
            .then((res) => {
              log('[%s] data loaded: %o', friendlyName(), res);
              if (opts && opts.initial) {
                _registerCacheWatchers();
              }
              touchLastLoaded();
              resolve();
            })
            .catch(onErr);
        }, delayVal);
      } catch (e) {
        onErr(e);
      }
    });
  };

  const loadDataThrottled = throttle(loadData, 200);

  return {
    loadData,
    loadDataThrottled,
    touchLastLoaded,
    handleError,
  };
}
